/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import type { ThemeType } from "@packages/ui";
import { ArrowLeftIcon, CloseIcon, IconButton } from "@packages/ui";
import * as RadixUIDialog from "@radix-ui/react-dialog";
import * as RadixUIProgress from "@radix-ui/react-progress";
import { motion } from "motion/react";
import { useMemo } from "react";

import { Durations } from "../../lib/enums/durations";
import { Step, useContext } from "./Creation";

const PROGRESS_END = 100;
const PROGRESS_HALF = 60;
const PROGRESS_START = 5;

const cssDiv: Interpolation<ThemeType> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.xs,
  width: "100%",
});
const cssProgressIndicator: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.accentMedium,
  borderRadius: theme.radius.full,
  height: "100%",
});
const cssProgressRoot: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.neutralsLowest,
  borderRadius: theme.radius.full,
  height: theme.spacing.s,
  width: "100%",
});

export const CreationHeader = () => {
  const { form, setStep, step } = useContext();

  const progress = useMemo(() => {
    if (step === Step.Name) return PROGRESS_START;

    if (form.formState.isSubmitSuccessful) return PROGRESS_END;

    return PROGRESS_HALF;
  }, [form.formState.isSubmitSuccessful, step]);

  return (
    <div css={cssDiv}>
      {step === Step.Name ? (
        <RadixUIDialog.Close asChild>
          <IconButton
            icon={<CloseIcon size={20} />}
            variant="transparentPrimary"
          />
        </RadixUIDialog.Close>
      ) : (
        <IconButton
          icon={<ArrowLeftIcon size={20} />}
          onClick={() => setStep(Step.Name)}
          variant="transparentPrimary"
        />
      )}
      <RadixUIProgress.Root css={cssProgressRoot} max={100} value={progress}>
        <RadixUIProgress.Indicator asChild css={cssProgressIndicator}>
          <motion.div
            animate={{ width: `${progress}%` }}
            initial={{ width: `${progress}%` }}
            transition={{
              duration: Durations.Short,
              ease: "easeOut",
              type: "tween",
            }}
          />
        </RadixUIProgress.Indicator>
      </RadixUIProgress.Root>
    </div>
  );
};
